import React, { useState } from 'react'
import type { AccountHolderAddress } from '@mobi/api-types'
import { formatAddress, createFullSuburb } from '@mobi/utils'
import { AddressManualEntry } from './AddressManualEntry'
import { ManualInputTextStyled } from './AddressInput.styled'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import {
  AutocompleteInputField,
  AutocompleteInputProps,
} from '@mobi/component-library/Legacy/Input/AutocompleteInputField/AutocompleteInputField'
import {
  getRequiredStreetNumberValidationMessage,
  getRequiredValidationMessage,
} from '@mobi/account/Areas/SignUp/Utils'
import { useAutoCompleteAddressList } from './Hooks/useAutoCompleteAddressList'
import { useSplitAddress } from './Hooks/useSplitAddress'
import type { ValidationResult } from './types'

type EntryMode = 'Autocomplete' | 'Manual'

export type AddressInputProps = {
  prefix?: string
  address?: AccountHolderAddress
  defaultEntryMode?: EntryMode
  required?: boolean
  onEntryModeChanged?: (mode: EntryMode) => void
  onValidationChange: (result: ValidationResult<AccountHolderAddress>) => void
  isDisabled?: boolean
}

export const AddressInput = ({
  prefix = '',
  address,
  defaultEntryMode = 'Autocomplete',
  required = true,
  onEntryModeChanged,
  onValidationChange,
  isDisabled = false,
}: AddressInputProps) => {
  const searchAddresses = useAutoCompleteAddressList()
  const [entryMode, setEntryMode] = useState<EntryMode>(defaultEntryMode)
  const [autocompleteValidationMessage, setAutocompleteValidationMessage] = useState('')

  const splitAddress = useSplitAddress({
    onSuccess: (addressDetails: AccountHolderAddress) => {
      const validationMessage = !addressDetails.streetNumber
        ? 'We require a street number. Please try another address or enter your address manually.'
        : ''
      setAutocompleteValidationMessage(validationMessage)
      onValidationChange({
        isValid: !validationMessage,
        result: addressDetails,
      })
    },
    onError: () => {
      setAutocompleteValidationMessage(
        'Unable to confirm address. Please try again or enter manually.'
      )
      onValidationChange({
        isValid: false,
      })
    },
  })

  const onSelectAutocompleteAddress = (addressMonikerId: string) => {
    setAutocompleteValidationMessage('')
    splitAddress.mutate(addressMonikerId)
  }

  if (entryMode === 'Autocomplete') {
    return (
      <div>
        <AutocompleteInputField
          name='Address'
          id={`${prefix}address`}
          inputName={`${prefix}address`}
          placeholder='Enter address'
          autoComplete='street-address'
          defaultValue={
            address
              ? formatAddress({
                  unitNumber: address.unitNumber,
                  streetNumber: address.streetNumber,
                  streetName: address.streetName,
                  streetType: address.streetType,
                  suburbStateAndPostcode: createFullSuburb(address),
                })
              : ''
          }
          required={required}
          fetchSuggestionsCallback={searchAddresses}
          onSelect={addressMonikerId => onSelectAutocompleteAddress(addressMonikerId)}
          onUnselect={() => onValidationChange({ isValid: false })}
          renderInput={({
            inputName,
            name,
            ...props
          }: Omit<AutocompleteInputProps, 'defaultValue' | 'type'>) => (
            <Input
              {...props}
              name={inputName ?? 'autocomplete'}
              label={name}
              autoComplete='new-password'
              isDisabled={isDisabled || splitAddress.isPending}
              onFocus={() => setAutocompleteValidationMessage('')}
              onBlur={() => {
                const validationMessage =
                  getRequiredValidationMessage(props.value) ||
                  getRequiredStreetNumberValidationMessage(splitAddress.data?.streetNumber)
                setAutocompleteValidationMessage(validationMessage)
              }}
              isInvalid={!!autocompleteValidationMessage}
              helpText={autocompleteValidationMessage}
            />
          )}
        />
        <ManualInputTextStyled>
          <span
            role='button'
            tabIndex={0}
            onClick={event => {
              setEntryMode('Manual')
              onEntryModeChanged?.('Manual')
              event.preventDefault()
            }}
          >
            Enter your address manually
          </span>
        </ManualInputTextStyled>
      </div>
    )
  }

  if (entryMode === 'Manual') {
    return (
      <>
        <AddressManualEntry
          inputNamePrefix={prefix}
          onValidationChange={onValidationChange}
          isDisabled={isDisabled}
        />

        <ManualInputTextStyled>
          <span
            role='button'
            tabIndex={0}
            onClick={event => {
              setEntryMode('Autocomplete')
              onEntryModeChanged?.('Autocomplete')
              event.preventDefault()
            }}
          >
            Search for your address
          </span>
        </ManualInputTextStyled>
      </>
    )
  }

  return null
}
